import { Hyperlink } from '../core/interfaces/Hyperlink';
import { Icon, SVGIcon } from '../core/interfaces/Icon';
import { PositionLeft, PositionRight } from '../core/types/globalTypes';

export enum DropdownErrors {
  INVALID_DATA = 'Data is unspecified, empty or invalid',
}
/**
 * A collection of dropdown item types
 */
export enum DropdownItemTypes {
  ACTION = 'action',
  ACTIONGROUP = 'actionGroup',
  DIVIDER = 'divider',
}

export type DropdownItemTypeAction = 'action';
export type DropdownItemTypeActionGroup = 'actionGroup';
export type DropdownItemTypeDivider = 'divider';

/**
 * The base interface for a dropdown item (group or option)
 */
export interface DropdownItemBase {
  text: string;
  disabled?: boolean;
  icon?: Icon | SVGIcon;
}

/**
 * Describes a dropdown divider object
 * @docs
 */
export type DropdownDivider = {
  type: DropdownItemTypeDivider;
};

/**
 * Describes a dropdown action object
 * @docs
 */
export type DropdownOption = {
  type: DropdownItemTypeAction;
  value: string | number;
  link?: Hyperlink;
} & DropdownItemBase;

/**
 * Describes a dropdown group object
 * @docs
 */
export type DropdownOptionGroup = {
  type: DropdownItemTypeActionGroup;
  items: (DropdownOption | DropdownDivider)[];
} & DropdownItemBase;

/**
 * General type for all dropdown cases
 * Type is hardcoded by design so it's impossible to mix a type with the wrong payload
 * @docs
 */
export type DropdownItem = DropdownDivider | DropdownOptionGroup | DropdownOption;

export type DropdownAlignment = PositionLeft | PositionRight;

/**
 * An interface for the custom `dropdownBlur` event
 * @docs
 */
export interface DropdownBlurEvent {
  evt: FocusEvent;
  value: string | number;
}

/**
 * An interface for the custom `dropdownChange` event
 * @docs
 */
export interface DropdownChangeEvent {
  evt: Event;
  value: string | number;
}

/**
 * An interface for the custom `dropdownFocus` event
 * @docs
 */
export interface DropdownFocusEvent {
  evt: FocusEvent;
  value: string | number;
}

/**
 * An interface for the custom `openChange` event for the dropdown
 * @docs
 */
export interface OpenChangeEvent {
  isOpen: boolean;
}

export type DropdownBlurHandler = (evt: DropdownBlurEvent) => void;

export type DropdownChangeHandler = (evt: DropdownChangeEvent) => void;

export type DropdownFocusHandler = (evt: DropdownFocusEvent) => void;

export type OpenChangeHandler = (evt: OpenChangeEvent) => void;
